import { Color, Spacing } from '@persona-id/design-tokens';
import {
  ChevronRightIcon,
  CircleInfoIcon,
  CopyIcon,
  EditIcon,
  MoreIcon,
  TrashIcon,
} from '@persona-id/design-tokens/icons';
import { snakeCase, startCase, without } from 'lodash/fp';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { ErrorType, MAXIMUM_DEPTH } from 'dashboard/components/PlatformSchemaEditor/constants';
import SchemaProperties from 'dashboard/components/PlatformSchemaEditor/SchemaProperties';
import { TypeSelect } from 'dashboard/components/PlatformSchemaEditor/TypeSelect';
import {
  createNewProperty,
  duplicateProperty,
  getDefaultSchemaForType,
  hasNesting,
} from 'dashboard/components/PlatformSchemaEditor/utils';
import ExpandableFieldRow, {
  NEST_SPACING,
} from 'dashboard/components-lib/FieldSchemaEditor/ExpandableFieldRow';
import { Inserter } from 'dashboard/components-lib/FieldSchemaEditor/Inserter';
import {
  getPlatformObjectTypeFromJsonSchema,
  getPlatformTemplateFromJsonSchema,
  getTypeDisplayText,
  isPlatformObjectSchema,
  PlatformJsonSchema,
} from 'dashboard/lib/workflows/platform-object';
import { WorkflowFieldObjectKey } from 'dashboard/models/workflow';
import Button from 'design-system/components/Button';
import Checkbox from 'design-system/components/Checkbox';
import DropdownMenu from 'design-system/components/DropdownMenu';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { TableBody, TableCell, TableRow } from 'design-system/components/Table';
import { Span } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip/Tooltip';
import { isFocused, isHovered, isNotDisabled } from 'design-system/lib/pseudoClass';
import { TransitionDuration } from 'design-system/variables/transitions';
import useToggle from 'lib/hooks/useToggle';
import {
  isJsonSchemaArray,
  isJsonSchemaObject,
  JsonSchema,
  JsonSchemaTypes,
} from 'lib/json-schema';

const StyledTableBody = styled(TableBody)`
  background-color: ${({ theme }) => theme.color.background.primary};
  width: 100%;
`;

const HighlightableTableRow = styled(TableRow)<{ expandable: boolean }>`
  ${({ expandable }) => (expandable ? 'cursor: pointer' : '')};

  &${isFocused} {
    z-index: 1;
    position: relative;
    box-shadow: inset 0 0 0 3px ${({ theme }) => theme.color.variants.primary.highlight};
  }
`;

const HighlightableTableCell = styled(TableCell)<{ noBorder: boolean }>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 6px;

  ${({ noBorder }) => (noBorder ? 'border: 0' : '')}
`;

const StyledExpandButton = styled(Button)`
  width: 24px;
  background-color: transparent;

  &${isHovered}${isNotDisabled} {
    box-shadow: none;
  }
`;

const StyledChevronIcon = styled(ChevronRightIcon)<{ expanded: number }>`
  transform: rotate(${({ expanded }) => (expanded ? '90deg' : '0')});
  transition: transform ${TransitionDuration.Fast};
  margin-right: ${Spacing[1]};
`;

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ReadOnlyContainer = styled.div<{ hasError: boolean }>`
  padding: 9px 6px;

  ${({ hasError, theme }) => (hasError ? `color: ${theme.color.variants.danger.default}` : '')}
`;

const Italics = styled(Span)`
  font-style: italic;
`;

const DoubleInputContainer = styled(FlexContainer)<{ isEditing: boolean }>`
  ${({ isEditing }) => (isEditing ? 'width: 100%' : `flex: 1; padding-left: ${Spacing[1]}`)};
`;

const InputContainer = styled.div<{ isLeft?: boolean; isRight?: boolean }>`
  width: 100%;

  & > button {
    border-top-left-radius: ${({ isRight }) => (isRight ? 0 : Spacing[1])};
    border-bottom-left-radius: ${({ isRight }) => (isRight ? 0 : Spacing[1])};
    border-top-right-radius: ${({ isLeft }) => (isLeft ? 0 : Spacing[1])};
    border-bottom-right-radius: ${({ isLeft }) => (isLeft ? 0 : Spacing[1])};
  }
`;

const RedText = styled.span`
  color: ${Color.CoreRed600};
`;

const IndicatorSpacer = styled.div.attrs(() => ({ 'aria-hidden': true }))<{ nestLevel: number }>`
  width: ${({ nestLevel }) => (NEST_SPACING - 1) * nestLevel + 15}px;
`;

const StyledCheckbox = styled(Checkbox)`
  padding-left: ${Spacing[1]};
  flex-grow: 1;
`;

const MenuItemIcon = styled.span`
  margin-right: ${Spacing[1]};
  vertical-align: middle;
`;

const PaddedSpan = styled.span`
  display: flex;
  padding: 0 ${Spacing[1]};
`;

const TooltipContainer = styled.div`
  margin-left: ${Spacing[1]};
  color: ${({ theme }) => theme.color.text.secondary};
`;

interface TemplateTooltipProps {
  type: WorkflowFieldObjectKey | null;
  template: string | null;
  templateOptionsByObjectKey: Partial<
    Record<WorkflowFieldObjectKey, { label: string; value: string }[]>
  >;
}

const TemplateTooltip: FC<TemplateTooltipProps> = ({
  type,
  template,
  templateOptionsByObjectKey,
}) => {
  const templateOption =
    type && templateOptionsByObjectKey[type]?.find(({ value }) => value === template)?.label;
  const portalRef = useRef(null);

  return type && templateOptionsByObjectKey[type] ? (
    <TooltipContainer ref={portalRef}>
      {/* Have to use a portal container since this exists on a modal with a higher z-index than the default tooltip */}
      <Tooltip
        portal
        portalProps={{ container: portalRef.current }}
        side='right'
        iconTrigger={<CircleInfoIcon size={10} />}
      >
        {templateOption ? `Limited to ${templateOption}` : 'All templates allowed'}
      </Tooltip>
    </TooltipContainer>
  ) : (
    <></>
  );
};

interface PropertyRowProps {
  editableId?: string;
  hasError: boolean;
  id: string;
  includePersonaObjects: boolean;
  nestLevel: number;
  prefix: string;
  property: string;
  requiredProps: string[];
  schema: PlatformJsonSchema;
  templateOptionsByObjectKey: Partial<
    Record<WorkflowFieldObjectKey, { label: string; value: string }[]>
  >;
  onAddProperty: (
    newProperty: {
      id: string;
      name: string;
      schema: PlatformJsonSchema;
    } | null,
  ) => void;
  onChangeProperty: (property: string) => void;
  onChangeSchema: (schema: JsonSchema) => void;
  onDeleteProperty: () => void;
  onUpdateRequired: (newRequiredProps: string[]) => void;
  setEditableId: (id: string | undefined) => void;
  setOutsideClickable: (val: boolean) => void;
  addError: (id: string, error: ErrorType[]) => void;
}

const PropertyRow: FC<PropertyRowProps> = ({
  editableId,
  hasError,
  id,
  includePersonaObjects,
  nestLevel = 0,
  property,
  prefix,
  requiredProps,
  schema,
  templateOptionsByObjectKey,
  onAddProperty,
  onChangeProperty,
  onChangeSchema,
  onDeleteProperty,
  onUpdateRequired,
  setEditableId,
  setOutsideClickable,
  addError,
}) => {
  const [propertyVal, setPropertyVal] = useState(property);
  const [expanded, { enable: expandSubRows, toggle: toggleExpansion }] = useToggle();
  const [isHovering, { enable: setIsHovering, disable: setStopHovering }] = useToggle(false);
  const [isMenuOpened, { toggle: setIsMenuOpened }] = useToggle(false);
  const [childrenErrors, setChildrenErrors] = useState<Record<string, boolean>>({});
  const expandable = useMemo(() => hasNesting(schema), [schema]);
  const hideBorder = expandable && expanded;
  const valueForSelect = getTypeDisplayText(schema) || 'None';
  const fullKey = prefix ? `${prefix}.${property}` : property;
  const isRequired = useMemo(() => requiredProps.includes(property), [requiredProps, property]);
  const isEditing = editableId === id || hasError;

  const wrappedOnChangeSchema = useCallback(
    (newSchema: JsonSchema) => {
      onChangeSchema(newSchema);
      setStopHovering();
    },
    [onChangeSchema, setStopHovering],
  );

  const addChildrenErrors = useCallback(
    (id: string, errors: ErrorType[]) => {
      if (errors.length > 0 !== childrenErrors[id]) {
        setChildrenErrors({
          ...childrenErrors,
          [id]: errors.length > 0,
        });
      }

      addError(id, errors);
    },
    [addError, childrenErrors],
  );

  const deleteProperty = useCallback(
    (e) => {
      e.stopPropagation();
      onDeleteProperty();
      if (isRequired) {
        onUpdateRequired(without([property], requiredProps));
      }
    },
    [isRequired, onDeleteProperty, onUpdateRequired, property, requiredProps],
  );

  const hasChildrenErrors = useMemo(
    () => Object.values(childrenErrors).some(Boolean),
    [childrenErrors],
  );

  const subRows = useMemo(() => {
    switch (schema.type) {
      case JsonSchemaTypes.Object: {
        return (
          !isPlatformObjectSchema(schema) &&
          (Object.entries(schema.properties).length > 0 ? (
            <SchemaProperties
              editableId={editableId}
              jsonSchema={schema}
              nestLevel={nestLevel + 1}
              prefix={fullKey}
              includePersonaObjects={includePersonaObjects}
              templateOptionsByObjectKey={templateOptionsByObjectKey}
              setEditableId={setEditableId}
              setOutsideClickable={setOutsideClickable}
              updateJsonSchema={onChangeSchema}
              addError={addChildrenErrors}
            />
          ) : (
            <Inserter
              prefix={fullKey}
              colSpan={3}
              label={`Add a field into ${fullKey}`}
              nestSpacing={NEST_SPACING * nestLevel}
              onAddRow={() => {
                const newProp = createNewProperty();
                onChangeSchema({
                  ...schema,
                  properties: {
                    ...schema.properties,
                    [newProp.name]: newProp.schema,
                  },
                });
              }}
            />
          ))
        );
      }
      case JsonSchemaTypes.Array:
        return (
          isJsonSchemaObject(schema.items) &&
          (Object.entries(schema.items.properties).length > 0 ? (
            <SchemaProperties
              editableId={editableId}
              jsonSchema={schema.items}
              nestLevel={nestLevel + 1}
              prefix={fullKey}
              includePersonaObjects={includePersonaObjects}
              templateOptionsByObjectKey={templateOptionsByObjectKey}
              setEditableId={setEditableId}
              setOutsideClickable={setOutsideClickable}
              updateJsonSchema={(newSchema) =>
                onChangeSchema({
                  ...schema,
                  items: newSchema,
                })
              }
              addError={addChildrenErrors}
            />
          ) : (
            <Inserter
              prefix={fullKey}
              colSpan={3}
              label={`Add a field into ${fullKey}`}
              nestSpacing={NEST_SPACING * nestLevel}
              onAddRow={() => {
                const newProp = createNewProperty();
                onChangeSchema({
                  ...schema,
                  items: {
                    type: JsonSchemaTypes.Object,
                    properties: {
                      [newProp.name]: newProp.schema,
                    },
                  },
                });
              }}
            />
          ))
        );
      default:
    }
  }, [
    schema,
    editableId,
    nestLevel,
    fullKey,
    includePersonaObjects,
    templateOptionsByObjectKey,
    setEditableId,
    setOutsideClickable,
    addChildrenErrors,
    onChangeSchema,
  ]);

  const normalizeName = () => {
    const formattedProperty = snakeCase(propertyVal);
    setPropertyVal(formattedProperty);

    if (formattedProperty !== property) {
      onChangeProperty(formattedProperty);

      if (isRequired) {
        onUpdateRequired([...without([property], requiredProps), formattedProperty]);
      }
    }
  };
  return (
    <StyledTableBody id={id} aria-label={`Details for ${id}`}>
      <HighlightableTableRow
        id={id}
        aria-label={`Row for ${id}`}
        tabIndex={0}
        expandable={expandable}
        onClick={() => setEditableId(id)}
        onMouseOver={setIsHovering}
        onMouseLeave={setStopHovering}
      >
        <HighlightableTableCell aria-label='Property' noBorder={hideBorder}>
          <FlexContainer>
            <Spacer width={4 * nestLevel} height={0} />
            {expandable && (
              <StyledExpandButton
                id={`property-row__${property}-toggle`}
                role='button'
                aria-label='Toggle row'
                width='icon'
                variant='inverse'
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpansion();
                }}
              >
                <StyledChevronIcon size={20} expanded={expanded ? 1 : 0} />
              </StyledExpandButton>
            )}
            {isEditing ? (
              <Input
                type='text'
                value={propertyVal}
                aria-label='Edit property name'
                aria-invalid={hasError}
                autoFocus
                hasError={hasError}
                onChange={(e) => setPropertyVal(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    normalizeName();
                    setEditableId(undefined);
                  }
                }}
                onBlur={normalizeName}
              />
            ) : (
              <ReadOnlyContainer
                aria-label='Property name'
                hasError={hasError || hasChildrenErrors}
              >
                {propertyVal ? (
                  <>
                    {propertyVal} {isRequired && <RedText>*</RedText>}
                  </>
                ) : (
                  <Italics color='secondary'>No name</Italics>
                )}
              </ReadOnlyContainer>
            )}
          </FlexContainer>
        </HighlightableTableCell>
        <HighlightableTableCell aria-label='Type' noBorder={hideBorder}>
          <DoubleInputContainer isEditing={isEditing}>
            {isEditing ? (
              <InputContainer isLeft={isJsonSchemaArray(schema)}>
                <TypeSelect
                  value={valueForSelect}
                  template={getPlatformTemplateFromJsonSchema(schema)}
                  templateOptionsByObjectKey={templateOptionsByObjectKey}
                  includePersonaObject={includePersonaObjects}
                  allowNesting={nestLevel < MAXIMUM_DEPTH - 1}
                  allowArray={nestLevel === 0}
                  onChange={(
                    newType: JsonSchemaTypes | WorkflowFieldObjectKey,
                    template?: string,
                  ) => {
                    const newSchema = getDefaultSchemaForType(newType, template);
                    if (newSchema) {
                      wrappedOnChangeSchema(newSchema);
                      hasNesting(newSchema) && expandSubRows();
                    }
                  }}
                  setOutsideClickable={setOutsideClickable}
                />
              </InputContainer>
            ) : (
              <>
                <Sticker aria-label='Type name'>{startCase(valueForSelect)}</Sticker>
                <TemplateTooltip
                  type={getPlatformObjectTypeFromJsonSchema(schema)}
                  template={getPlatformTemplateFromJsonSchema(schema)}
                  templateOptionsByObjectKey={templateOptionsByObjectKey}
                />
              </>
            )}

            {isJsonSchemaArray(schema) &&
              (isEditing ? (
                <InputContainer isRight>
                  <TypeSelect
                    value={
                      (isPlatformObjectSchema(schema.items)
                        ? getPlatformObjectTypeFromJsonSchema(schema.items)
                        : schema.items?.type) || ''
                    }
                    template={getPlatformTemplateFromJsonSchema(schema.items)}
                    templateOptionsByObjectKey={templateOptionsByObjectKey}
                    includePersonaObject={includePersonaObjects}
                    allowNesting={nestLevel < MAXIMUM_DEPTH - 1}
                    allowArray={false}
                    label='Select array item type'
                    onChange={(
                      newType: JsonSchemaTypes | WorkflowFieldObjectKey,
                      template?: string,
                    ) => {
                      const newSchema = getDefaultSchemaForType(newType, template);
                      if (newSchema) {
                        wrappedOnChangeSchema({
                          ...schema,
                          items: newSchema,
                        });
                        hasNesting(newSchema) && expandSubRows();
                      }
                    }}
                    setOutsideClickable={setOutsideClickable}
                  />
                </InputContainer>
              ) : (
                <>
                  <PaddedSpan>of</PaddedSpan>
                  <Sticker aria-label='Array item type name'>
                    {startCase(getTypeDisplayText(schema.items))}
                  </Sticker>
                  <TemplateTooltip
                    type={getPlatformObjectTypeFromJsonSchema(schema.items)}
                    template={getPlatformTemplateFromJsonSchema(schema.items)}
                    templateOptionsByObjectKey={templateOptionsByObjectKey}
                  />
                </>
              ))}
          </DoubleInputContainer>
        </HighlightableTableCell>
        <HighlightableTableCell width='100px' aria-label='Required' noBorder={hideBorder}>
          <FlexContainer>
            <StyledCheckbox
              aria-label='Toggle property required'
              checked={isRequired}
              onChange={(e) => {
                e.target.checked
                  ? onUpdateRequired([...requiredProps, property])
                  : onUpdateRequired(without([property], requiredProps));
              }}
              containerProps={{
                onClick: (e) => {
                  e.stopPropagation();
                },
              }}
            />
            {!isEditing && !hasError && !hasChildrenErrors && (isHovering || isMenuOpened) && (
              <DropdownMenu
                onOpenChange={setIsMenuOpened}
                trigger={
                  <Button
                    id='property-row__more'
                    aria-label={`More actions for ${property}`}
                    size='sm'
                    variant='inverse'
                    width='icon'
                    onClick={(e) => {
                      // Avoid clicking table row
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <MoreIcon />
                  </Button>
                }
              >
                <DropdownMenu.Item
                  id='property-row__more-edit'
                  aria-label='Edit property'
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditableId(id);
                  }}
                >
                  <MenuItemIcon>
                    <EditIcon size={16} />
                  </MenuItemIcon>
                  Edit
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  id='property-row__more-duplicate'
                  aria-label='Duplicate property'
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddProperty(duplicateProperty(property, schema));
                    setStopHovering();
                  }}
                >
                  <MenuItemIcon>
                    <CopyIcon size={16} />
                  </MenuItemIcon>
                  Duplicate
                </DropdownMenu.Item>

                <DropdownMenu.Item
                  id='property-row__more-delete'
                  aria-label='Delete property'
                  onClick={deleteProperty}
                >
                  <RedText>
                    <MenuItemIcon>
                      <TrashIcon size={16} />
                    </MenuItemIcon>
                    Delete
                  </RedText>
                </DropdownMenu.Item>
              </DropdownMenu>
            )}
            {isEditing && !hasError && !hasChildrenErrors && (
              <Button
                id='property-row__delete'
                aria-label='Delete property'
                variant='inverse'
                size='sm'
                width='icon'
                onClick={deleteProperty}
              >
                <TrashIcon />
              </Button>
            )}
          </FlexContainer>
        </HighlightableTableCell>
      </HighlightableTableRow>
      {expandable && (
        <ExpandableFieldRow
          colSpan={3}
          nestLevel={nestLevel}
          maxDepth={MAXIMUM_DEPTH}
          indicatorSpacer={<IndicatorSpacer nestLevel={nestLevel} />}
          visible={expanded}
          showBottomBorder={false}
        >
          {subRows}
        </ExpandableFieldRow>
      )}
      {nestLevel < MAXIMUM_DEPTH && !expanded && (
        <Inserter
          prefix={fullKey}
          colSpan={3}
          nestSpacing={NEST_SPACING * nestLevel}
          onAddRow={() => {
            onAddProperty(null);
          }}
        />
      )}
    </StyledTableBody>
  );
};

export default PropertyRow;
